import {
  Shield, Paintbrush, DoorOpen, Layers, Droplets,
  MapPin, Clock, GraduationCap, Building2, Plane, Train, Hospital
} from "lucide-react";

const specifications = [
  {
    icon: Shield,
    title: "Structure",
    desc: "Reinforced shear walls for external walls & combination of brick masonry and shear walls for internal walls",
  },
  {
    icon: Paintbrush,
    title: "Painting",
    desc: "Premium acrylic emulsion paint with smooth putty finish internally; Weather-proof exterior emulsion",
  },
  {
    icon: DoorOpen,
    title: "Main Door",
    desc: "10-feet height premium designer door with biometric mortise lock (Yale make), 48mm engineered flush shutter with PU polish",
  },
  {
    icon: Layers,
    title: "Flooring",
    desc: "Engineered wooden flooring in master bedroom & multipurpose room; 18mm granite / full body tiles for staircase",
  },
  {
    icon: Droplets,
    title: "Bathrooms",
    desc: "450x600mm dado tiles, anti-skid flooring, Parryware CP & sanitary fittings or equivalent",
  },
  {
    icon: DoorOpen,
    title: "Internal Doors",
    desc: "8-feet height with premium designer hardware, oak veneer flush shutter without polish",
  },
];

const locations = [
  { icon: GraduationCap, name: "Candiidus International School", time: "6 mins" },
  { icon: GraduationCap, name: "The Gaudium School", time: "9 mins" },
  { icon: Building2, name: "Value Zone Hyper Mart", time: "10 mins" },
  { icon: Hospital, name: "Dhruva Hospital", time: "13 mins" },
  { icon: Train, name: "Proposed Metro Station", time: "15 mins" },
  { icon: Hospital, name: "Pranam Hospital", time: "17 mins" },
  { icon: Hospital, name: "Shraddha Children's Hospital", time: "18 mins" },
  { icon: Building2, name: "ICRISAT", time: "20 mins" },
  { icon: GraduationCap, name: "Samashti International School", time: "20 mins" },
  { icon: Building2, name: "Financial District", time: "25 mins" },
  { icon: Building2, name: "Neopolis", time: "20 mins" },
  { icon: Plane, name: "Rajiv Gandhi International Airport", time: "40 mins" },
];

export default function SpecsLocation() {
  return (
    <section
      id="location"
      data-testid="specs-location-section"
      className="py-20 md:py-32 bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Specifications */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <p className="overline mb-4">Premium Quality</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
              Built to <span className="italic text-[#C5A059]">endure</span>.
            </h2>
            <div className="gold-line mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specifications.map((spec, i) => (
              <div
                key={spec.title}
                data-testid={`spec-${i}`}
                className="bg-white border border-[#E8E4D9] p-8 group hover:border-[#C5A059]/40 transition-all duration-500"
              >
                <spec.icon
                  size={28}
                  strokeWidth={1.5}
                  className="text-[#C5A059] mb-5 group-hover:scale-110 transition-transform duration-300"
                />
                <h4 className="font-serif text-lg text-[#1A1A1A] mb-3">{spec.title}</h4>
                <p className="text-sm text-[#5C5C59] leading-relaxed">{spec.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Location */}
        <div>
          <div className="text-center mb-16">
            <p className="overline mb-4">Strategic Location</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
              Connected to what
              <br />
              <span className="italic text-[#C5A059]">matters most</span>.
            </h2>
            <div className="gold-line mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Map placeholder */}
            <div className="bg-white border border-[#E8E4D9] p-2">
              <div className="w-full h-[400px] bg-[#FAF9F6] flex items-center justify-center relative overflow-hidden">
                <iframe
                  title="La Valora Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3854.2780540491362!2d78.2177651!3d17.520053900000004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcbf30031109953%3A0x5bdb09bf9c1639b8!2sRevasa%20La%20Valora!5e1!3m2!1sen!2sin!4v1775059443281!5m2!1sen!2sin"
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="p-4 flex items-center gap-2">
                <MapPin size={16} className="text-[#C5A059]" />
                <span className="text-sm text-[#5C5C59]">Kardanur, Telangana 502300</span>
              </div>
            </div>

            {/* Location List */}
            <div>
              <div className="space-y-0">
                {locations.map((loc, i) => (
                  <div
                    key={i}
                    data-testid={`location-${i}`}
                    className="flex items-center justify-between py-4 border-b border-[#E8E4D9]/60 group"
                  >
                    <div className="flex items-center gap-3">
                      <loc.icon size={18} className="text-[#C5A059] flex-shrink-0" strokeWidth={1.5} />
                      <span className="text-[#1A1A1A] text-sm md:text-base">{loc.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <Clock size={14} className="text-[#C5A059]" />
                      <span className="text-sm font-semibold text-[#C5A059]">{loc.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
